/**
 * Message template engine - exact mirror of server/src/utils/messageTemplate.util.js.
 * Both are tested against server/src/utils/messageTemplate.vectors.json, so a preview is
 * always what gets sent.
 *
 *   {{first_name}}        lead value
 *   {{first_name|there}}  lead value, or "there" when the lead has none
 *   {Hi|Hello|Hey}        spintax: one option per lead, stable for that lead
 */

export const TEMPLATE_VARIABLE_KEYS = Object.freeze([
  "first_name",
  "last_name",
  "full_name",
  "company",
  "title",
  "sender_name",
]);

const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z_]+)\s*(?:\|([^}]*))?\}\}/g;
const SPINTAX_PATTERN = /(?<!\{)\{(?!\{)((?:[^{}]|\{\{[^{}]*\}\})*\|(?:[^{}]|\{\{[^{}]*\}\})*)\}(?!\})/g;

export function hashString(text) {
  let hash = 0x811c9dc5;
  const value = String(text);
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function splitSpintaxOptions(body) {
  const options = [""];
  const parts = body.split(/(\{\{[^{}]*\}\})/);
  for (const part of parts) {
    if (part.startsWith("{{")) {
      options[options.length - 1] += part;
      continue;
    }
    const pieces = part.split("|");
    options[options.length - 1] += pieces[0];
    for (let i = 1; i < pieces.length; i += 1) options.push(pieces[i]);
  }
  return options;
}

export function resolveSpintax(text, seed = "") {
  let occurrence = 0;
  return String(text ?? "").replace(SPINTAX_PATTERN, (_match, body) => {
    const options = splitSpintaxOptions(body);
    const index = hashString(`${seed}#${occurrence}#${body}`) % options.length;
    occurrence += 1;
    return options[index];
  });
}

export function getTemplateVariableValues(contact = {}, sender = {}) {
  const fullName = String(contact?.full_name ?? "").trim().replace(/\s+/g, " ");
  const [firstName = "", ...rest] = fullName ? fullName.split(" ") : [];
  return {
    first_name: firstName,
    last_name: rest.join(" "),
    full_name: fullName,
    company: String(contact?.company ?? "").trim(),
    title: String(contact?.designation ?? "").trim(),
    sender_name: String(sender?.name ?? "").trim(),
  };
}

export function renderTemplateVariables(text, values = {}) {
  return String(text ?? "").replace(VARIABLE_PATTERN, (match, rawName, fallback) => {
    const name = rawName.toLowerCase();
    if (!TEMPLATE_VARIABLE_KEYS.includes(name)) return match;
    return String(values[name] ?? "").trim() || String(fallback ?? "").trim();
  });
}

export function renderMessageTemplate(text, { values = {}, seed = "" } = {}) {
  return renderTemplateVariables(resolveSpintax(text, seed), values);
}

/** Seeds used by the server when sending (contact id + field). */
export const getMessageSeeds = (contact) => {
  const id = String(contact?.id ?? contact?.contact_id ?? "");
  return { subject: `${id}:subject`, body: `${id}:body` };
};

export function renderMessageForContact({ subject = "", content = "" }, contact, sender) {
  const values = getTemplateVariableValues(contact, sender);
  const seeds = getMessageSeeds(contact);
  return {
    subject: renderMessageTemplate(subject, { values, seed: seeds.subject }),
    content: renderMessageTemplate(content, { values, seed: seeds.body }),
  };
}

/**
 * Same rendering as renderMessageTemplate, split into segments so a preview can show which
 * parts came from the lead, from a fallback, or are missing.
 */
export function renderMessageSegments(text, { values = {}, seed = "" } = {}) {
  const resolved = resolveSpintax(text, seed);
  const segments = [];
  let last = 0;
  for (const match of resolved.matchAll(VARIABLE_PATTERN)) {
    if (match.index > last) segments.push({ type: "text", text: resolved.slice(last, match.index) });
    const name = match[1].toLowerCase();
    if (!TEMPLATE_VARIABLE_KEYS.includes(name)) {
      segments.push({ type: "unknown", text: match[0], name: match[1] });
    } else {
      const value = String(values[name] ?? "").trim();
      const fallback = String(match[2] ?? "").trim();
      segments.push({
        type: "variable",
        name,
        text: value || fallback,
        status: value ? "filled" : fallback ? "fallback" : "missing",
      });
    }
    last = match.index + match[0].length;
  }
  if (last < resolved.length) segments.push({ type: "text", text: resolved.slice(last) });
  return segments;
}

/** Variables used without a fallback: these become blank for leads missing the value. */
export function getVariablesWithoutFallback(text) {
  const names = new Set();
  for (const match of String(text ?? "").matchAll(VARIABLE_PATTERN)) {
    const name = match[1].toLowerCase();
    if (TEMPLATE_VARIABLE_KEYS.includes(name) && !String(match[2] ?? "").trim()) names.add(name);
  }
  return [...names];
}
